import { motion, useMotionTemplate, useMotionValue } from "motion/react";
import type React from "react";
import { forwardRef, useCallback, useEffect, useRef } from "react";
import { cn } from "utils/cn";

interface MagicCardProps {
	children?: React.ReactNode;
	className?: string;
	style?: React.CSSProperties;
	gradientSize?: number;
	gradientColor?: string;
	gradientOpacity?: number;
	gradientFrom?: string;
	gradientTo?: string;
}

export const MagicCard = forwardRef<HTMLDivElement, MagicCardProps>(
	function MagicCard(
		{
			children,
			className,
			style,
			gradientSize = 200,
			gradientColor = "var(--ant-color-primary-bg)",
			gradientOpacity = 0.8,
			gradientFrom = "var(--ant-color-primary)",
			gradientTo = "var(--ant-color-success)",
		},
		ref,
	) {
		const mouseX = useMotionValue(-gradientSize);
		const mouseY = useMotionValue(-gradientSize);
		const gradientSizeRef = useRef(gradientSize);

		useEffect(() => {
			gradientSizeRef.current = gradientSize;
		}, [gradientSize]);

		const reset = useCallback(() => {
			const off = -gradientSizeRef.current;
			mouseX.set(off);
			mouseY.set(off);
		}, [mouseX, mouseY]);

		const handlePointerMove = useCallback(
			(e: React.PointerEvent<HTMLDivElement>) => {
				const rect = e.currentTarget.getBoundingClientRect();
				mouseX.set(e.clientX - rect.left);
				mouseY.set(e.clientY - rect.top);
			},
			[mouseX, mouseY],
		);

		useEffect(() => {
			reset();
			const handleGlobalPointerOut = (e: PointerEvent) => {
				if (!e.relatedTarget) reset();
			};
			const handleBlur = () => reset();
			const handleVisibility = () => {
				if (document.visibilityState !== "visible") reset();
			};

			window.addEventListener("pointerout", handleGlobalPointerOut);
			window.addEventListener("blur", handleBlur);
			document.addEventListener("visibilitychange", handleVisibility);

			return () => {
				window.removeEventListener("pointerout", handleGlobalPointerOut);
				window.removeEventListener("blur", handleBlur);
				document.removeEventListener("visibilitychange", handleVisibility);
			};
		}, [reset]);

		return (
			<motion.div
				ref={ref}
				className={cn(
					"group relative isolate overflow-hidden rounded-[inherit] border border-transparent",
					className,
				)}
				onPointerMove={handlePointerMove}
				onPointerLeave={reset}
				style={{
					...style,
					background: useMotionTemplate`
					linear-gradient(var(--ant-color-bg-container) 0 0) padding-box,
					radial-gradient(${gradientSize}px circle at ${mouseX}px ${mouseY}px,
						${gradientFrom},
						${gradientTo},
						var(--ant-color-border) 100%
					) border-box
				`,
				}}
			>
				<div className="absolute inset-px z-20 rounded-[inherit] bg-[var(--ant-color-bg-container)]" />
				<motion.div
					className="pointer-events-none absolute inset-px z-30 rounded-[inherit] opacity-0 transition-opacity duration-300 group-hover:opacity-100"
					style={{
						background: useMotionTemplate`
						radial-gradient(${gradientSize}px circle at ${mouseX}px ${mouseY}px,
							${gradientColor},
							transparent 100%
						)
					`,
						opacity: gradientOpacity,
					}}
				/>
				<div className="relative z-40">{children}</div>
			</motion.div>
		);
	},
);
